/**
 * Project-page copy: titles, meta descriptions, the direct-answer paragraph
 * and the per-project FAQ, in every site language.
 *
 * Deliberately free of React and of any browser API, because it is imported by
 * BOTH the running app (src/pages/PropertyDetails.jsx) and the build-time
 * prerenderer (scripts/prerender.mjs). If the two ever generated different
 * text, Google would see one string in the raw HTML and another after
 * rendering — the classic cloaking-adjacent mismatch that gets snippets
 * rewritten or ignored.
 */

import { NAP } from './nap.js';
import {
    compactPrice,
    fullPrice,
    handoverLabel,
    parseHandover,
    isHandoverPast,
    cityName,
    categoryName,
    districtLabel,
    UAE,
    truncate
} from './seoCopy.js';

export { compactPrice, fullPrice, handoverLabel, isHandoverPast };

/**
 * Normalises a project from either API shape (list vs. detail) into the one
 * set of fields every SEO string is built from.
 */
export function normaliseProject(p = {}) {
    const district =
        p.district_name ||
        (Array.isArray(p.district_data) ? p.district_data[0]?.name : p.district_data?.name) ||
        '';
    const city = p.city_name || p.city_data?.name || 'Dubai';
    const category = Array.isArray(p.property_category)
        ? p.property_category[0]
        : (Array.isArray(p.property_types) ? p.property_types[0] : p.types) || '';

    return {
        id: p.id ?? p.slug ?? '',
        slug: p.slug || '',
        title: (p.title || p.meta_title || '').trim(),
        developer: (p.developer_name || '').trim(),
        district: String(district).trim(),
        city: String(city).trim(),
        // "JVC, Dubai" — the human-readable place, never the API's raw
        // plus-code blob ("26X4+J8P - Jumeirah Village - ...").
        location: [String(district).trim(), String(city).trim()].filter(Boolean).join(', ') || 'Dubai',
        priceMin: Number(p.min_price ?? p.price_start ?? 0) || 0,
        priceMax: Number(p.max_price ?? p.price_end ?? 0) || 0,
        areaMin: Number(p.min_area ?? p.area_start ?? 0) || 0,
        // The API returns "1,2" or "1,2,3" — a range reads better than a list
        // and is what a person would type ("2 bedroom apartment JVC").
        beds: bedsRange(p.beds),
        handover: p.expected_delivery_date || p.expected_completion_date || '',
        handoverPast: isHandoverPast(p.expected_delivery_date || p.expected_completion_date),
        status: p.status || p.project_status || '',
        category: String(category).trim(),
        image: p.feature_image || p.images?.feature || p.image || '',
        imageAlt: p.feature_image_alt_text || '',
        latLong: p.lat_long || p.latlong || '',
        description: String(p.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
        updatedAt: p.updated_at || p.format_updated_at || p.created_date || p.created_at || '',
        soldOut: /out\s*of\s*stock|sold\s*out/i.test(String(p.status || p.project_status || ''))
    };
}

/**
 * Should this project have an indexable page?
 *
 * The rule used to be "a price or live availability", which noindexed every
 * sold-out project without a price — 687 of them. That made sense while a
 * project page was a name and a price. It no longer is: a sold-out project
 * page carries the developer's description, unit types, floor plans, the
 * gallery, nearby places and alternatives in the same area — and sold-out
 * projects are exactly what people search by name ("creek palace residences",
 * "seascape dubai") long after launch.
 *
 * So a page is indexable when it has a name and something to say: a price,
 * live availability, or real content from its detail record (a description
 * of some length, units, or a gallery). Only a name with nothing behind it
 * stays noindex,follow — still prerendered and linked, never in the sitemap.
 */
export function isIndexable(project) {
    const p = normaliseProject(project);
    if (!p.id || !p.title) return false;
    if (p.priceMin > 0 || !p.soldOut) return true;
    const units = Array.isArray(project.typical_units) ? project.typical_units.length : 0;
    const images = Array.isArray(project.all_images) ? project.all_images.length : 0;
    return p.description.length >= 200 || units > 0 || images >= 3;
}

/**
 * The district, unless it merely repeats the project name.
 *
 * Several developments share their name with their district ("Remraam" in
 * Remraam, "Dubai Hills" in Dubai Hills), which produced "Remraam في Remraam"
 * — a phrase that reads like a template bug in the SERP snippet.
 */
function placeOf(p) {
    const t = p.title.toLowerCase().trim();
    const label = districtLabel(p.district);
    const d = label.toLowerCase();
    if (!d) return '';
    if (d === t || d.includes(t) || t.includes(d)) return '';
    return label;
}

/**
 * Whether "by {developer}" would only repeat the name: "Azizi Venice by
 * Azizi", "Damac Capital Bay by DAMAC".
 */
function devInName(p) {
    const d = p.developer.toLowerCase().replace(/\s+(properties|developments?|group|real estate|llc)$/i, '').trim();
    return !d || p.title.toLowerCase().includes(d);
}

/** "JVC, Dubai" in the page language; the city alone when the district repeats the name. */
function placeLine(p, lang) {
    const sep = lang === 'ar' || lang === 'ur' ? '، ' : lang === 'zh' ? '' : ', ';
    const city = cityName(p.city, lang);
    if (!placeOf(p)) return city;
    return lang === 'zh' ? `${city}${placeOf(p)}` : `${placeOf(p)}${sep}${city}`;
}

/**
 * The project's payment plan as percentages, from the detail record.
 *
 * new_payment_plans[0].heading_percentages is read first: the `info` block
 * folds the 4% DLD fee into the booking share (14 + 60 + 30 = 104 on a
 * 10/60/30 plan), which would misstate the plan. A plan only counts when its
 * parts add up to 100. The older payment_plans_json shape is the fallback.
 *
 * → { booking, construction, handover, post, postMonths } | null
 */
export function paymentPlan(project = {}) {
    const pct = (v) => {
        const n = parseFloat(String(v ?? '').replace('%', ''));
        return isFinite(n) && n > 0 ? n : 0;
    };
    const valid = (pl) => {
        const sum = pl.booking + pl.construction + pl.handover + pl.post;
        return sum >= 99 && sum <= 101 ? pl : null;
    };
    const classify = (label, value, into) => {
        const l = String(label).toLowerCase();
        if (/post|after/.test(l)) into.post += value;
        else if (/book|down|reserv/.test(l)) into.booking += value;
        else if (/construct|during|install/.test(l)) into.construction += value;
        else if (/hand|complet/.test(l)) into.handover += value;
    };

    const plan = Array.isArray(project.new_payment_plans) ? project.new_payment_plans[0] : null;
    if (plan) {
        const postMonths = pct(plan.info?.period_after_handover) || null;
        if (plan.heading_percentages && typeof plan.heading_percentages === 'object') {
            const pl = { booking: 0, construction: 0, handover: 0, post: 0, postMonths };
            for (const [k, v] of Object.entries(plan.heading_percentages)) classify(k, pct(v), pl);
            if (valid(pl)) return pl;
        }
        const i = plan.info || {};
        const pl = {
            booking: pct(i.on_booking_percent), construction: pct(i.on_construction_percent),
            handover: pct(i.on_handover_percent), post: pct(i.post_handover_percent), postMonths
        };
        if (valid(pl)) return pl;
    }

    const legacy = Array.isArray(project.payment_plans_json) ? project.payment_plans_json[0] : null;
    if (Array.isArray(legacy?.data)) {
        const pl = { booking: 0, construction: 0, handover: 0, post: 0, postMonths: null };
        for (const entry of legacy.data) {
            for (const [k, v] of Object.entries(entry || {})) classify(v, pct(k), pl);
        }
        if (valid(pl)) return pl;
    }
    return null;
}

/** "70/30 payment plan" / "10% on booking, 50% post-handover", per language. */
export function planPhrase(plan, lang = 'en') {
    if (!plan) return null;
    const b = plan.booking;
    if (plan.post) {
        return {
            ar: `${b}% عند الحجز و${plan.post}% بعد التسليم`,
            en: `${b}% on booking, ${plan.post}% post-handover`,
            ru: `${b}% при бронировании, ${plan.post}% после сдачи`,
            zh: `预订付${b}%，交付后付${plan.post}%`,
            ur: `بکنگ پر ${b}%، حوالگی کے بعد ${plan.post}%`
        }[lang];
    }
    const during = b + plan.construction;
    const split = `${during}/${plan.handover}`;
    return {
        ar: `خطة سداد ${split}`,
        en: `${split} payment plan`,
        ru: `план оплаты ${split}`,
        zh: `${split}付款计划`,
        ur: `${split} ادائیگی پلان`
    }[lang];
}

/**
 * The full breakdown as a sentence, for the answer paragraph and the FAQ:
 * "Payment plan: 10% on booking, 60% during construction, 30% on handover."
 */
export function planSentence(plan, lang = 'en') {
    if (!plan) return null;
    const parts = {
        ar: [plan.booking && `${plan.booking}% عند الحجز`, plan.construction && `${plan.construction}% خلال الإنشاء`,
            plan.handover && `${plan.handover}% عند التسليم`, plan.post && `${plan.post}% بعد التسليم${plan.postMonths ? ` على ${plan.postMonths} شهراً` : ''}`],
        en: [plan.booking && `${plan.booking}% on booking`, plan.construction && `${plan.construction}% during construction`,
            plan.handover && `${plan.handover}% on handover`, plan.post && `${plan.post}% after handover${plan.postMonths ? ` over ${plan.postMonths} months` : ''}`],
        ru: [plan.booking && `${plan.booking}% при бронировании`, plan.construction && `${plan.construction}% во время строительства`,
            plan.handover && `${plan.handover}% при сдаче`, plan.post && `${plan.post}% после сдачи${plan.postMonths ? ` на ${plan.postMonths} мес` : ''}`],
        zh: [plan.booking && `预订时${plan.booking}%`, plan.construction && `建设期间${plan.construction}%`,
            plan.handover && `交付时${plan.handover}%`, plan.post && `交付后${plan.post}%${plan.postMonths ? `（${plan.postMonths}个月内）` : ''}`],
        ur: [plan.booking && `بکنگ پر ${plan.booking}%`, plan.construction && `تعمیر کے دوران ${plan.construction}%`,
            plan.handover && `حوالگی پر ${plan.handover}%`, plan.post && `حوالگی کے بعد ${plan.post}%${plan.postMonths ? ` (${plan.postMonths} ماہ میں)` : ''}`]
    }[lang]?.filter(Boolean);
    if (!parts?.length) return null;
    return {
        ar: `خطة السداد: ${parts.join('، ')}.`,
        en: `Payment plan: ${parts.join(', ')}.`,
        ru: `План оплаты: ${parts.join(', ')}.`,
        zh: `付款计划：${parts.join('，')}。`,
        ur: `ادائیگی پلان: ${parts.join('، ')}۔`
    }[lang];
}

/** The H1: the project's name, framed in the page's language. */
export function projectH1(project, lang = 'ar') {
    const p = normaliseProject(project);
    if (!p.developer || devInName(p)) return p.title;
    return {
        ar: `${p.title} من ${p.developer}`,
        en: `${p.title} by ${p.developer}`,
        ru: `${p.title} от ${p.developer}`,
        zh: `${p.developer}开发的${p.title}`,
        ur: `${p.developer} کا ${p.title}`
    }[lang] || `${p.title} — ${p.developer}`;
}

const TITLE_MAX = 60;

/**
 * <title>: "{Project} by {Developer} | From AED {price} | Handover {date}",
 * and its equivalent in each language, kept to 60 characters.
 *
 * When the full form is too long it degrades in a fixed order — the
 * handover quarter becomes a year, then the developer goes, then the
 * handover — so the project name and the price, the two things that earn the
 * click, are the last to be cut. No brand suffix: the URL carries it.
 */
export function projectTitle(project, lang = 'ar') {
    const p = normaliseProject(project);
    const name = p.title || 'Dubai Off-Plan Project';
    const price = compactPrice(p.priceMin, lang);
    const ho = parseHandover(p.handover);

    const T = {
        ar: {
            by: (n, d) => `${n} من ${d}`, price: (x) => `يبدأ من ${x}`, soldOut: 'نفدت الوحدات',
            handover: (h) => `التسليم ${h}`, ready: 'جاهز للتسليم', year: (y) => String(y)
        },
        en: {
            by: (n, d) => `${n} by ${d}`, price: (x) => `From ${x}`, soldOut: 'Sold Out',
            handover: (h) => `Handover ${h}`, ready: 'Ready', year: (y) => String(y)
        },
        ru: {
            by: (n, d) => `${n} от ${d}`, price: (x) => `от ${x}`, soldOut: 'Распродано',
            handover: (h) => `сдача ${h}`, ready: 'Готов', year: (y) => `${y}`
        },
        zh: {
            by: (n, d) => `${n}｜${d}开发`, price: (x) => `${x}起`, soldOut: '已售罄',
            handover: (h) => `${h}交付`, ready: '现房', year: (y) => `${y}年`
        },
        ur: {
            by: (n, d) => `${d} کا ${n}`, price: (x) => `${x} سے`, soldOut: 'فروخت ہو چکا',
            handover: (h) => `حوالگی ${h}`, ready: 'تیار', year: (y) => `${y}`
        }
    }[lang] || {};
    const sep = lang === 'zh' ? '｜' : ' | ';

    const head = (withDev) => (withDev && p.developer && !devInName(p) ? T.by(name, p.developer) : name);
    const money = p.soldOut ? T.soldOut : price ? T.price(price) : (placeOf(p) || null);
    const when = (short) => {
        if (!ho) return null;
        if (ho.past) return p.soldOut ? null : T.ready;
        return T.handover(short ? T.year(ho.year) : ho.label[lang]);
    };

    const variants = [
        [head(true), money, when(false)],
        [head(true), money, when(true)],
        [head(false), money, when(false)],
        [head(false), money, when(true)],
        [head(true), money],
        [head(false), money],
        [head(false)]
    ];
    for (const v of variants) {
        const t = v.filter(Boolean).join(sep);
        if (t.length <= TITLE_MAX) return t;
    }
    return truncate(name, TITLE_MAX);
}

/**
 * Meta description, 150–160 characters (about 80 for Chinese, which Google
 * cuts by width, not count): place, price, payment plan, handover, units —
 * the facts that differ from one project to the next, most important first.
 *
 * Each slot lists its wordings longest first, and the first that still fits
 * is taken; a closing line, also longest first, pads the result toward 150
 * when the facts alone run short.
 */
export function projectDescription(project, lang = 'ar') {
    const p = normaliseProject(project);
    const price = fullPrice(p.priceMin, lang);
    const ho = parseHandover(p.handover);
    const handover = ho ? ho.label[lang] : null;
    const plan = planPhrase(paymentPlan(project), lang);
    const place = placeLine(p, lang);
    const cat = categoryName(p.category, lang);
    const dev = p.developer && !devInName(p) ? p.developer : '';
    const size = p.areaMin ? p.areaMin.toLocaleString('en-US') : '';
    const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

    const slots = {
        ar: [
            [`${p.title}${dev ? ` من ${dev}` : ''} في ${place}.`, `${p.title} في ${place}.`],
            [p.soldOut ? 'نفدت الوحدات حالياً.' : price && `يبدأ من ${price}.`],
            [plan && `${plan}.`],
            [handover && (ho.past ? `سُلّم ${handover}.` : `التسليم ${handover}.`), ho && (ho.past ? `سُلّم ${ho.year}.` : `التسليم ${ho.year}.`)],
            [(p.beds || size) && `${cat}${p.beds ? ` ${p.beds} غرف` : ''}${size ? ` من ${size} قدم مربع` : ''}.`, p.beds && `${cat} ${p.beds} غرف.`],
            p.soldOut
                ? ['اسأل عن البدائل في المنطقة نفسها.', 'اسأل عن البدائل.']
                : ['بسعر المطوّر الرسمي وبدون عمولة على المشتري.', 'بسعر المطوّر بدون عمولة.', 'بدون عمولة.']
        ],
        en: [
            [`${p.title}${dev ? ` by ${dev}` : ''} in ${place}.`, `${p.title} in ${place}.`],
            [p.soldOut ? 'Currently sold out.' : price && `From ${price}.`],
            [plan && `${cap(plan)}.`],
            [handover && (ho.past ? `Handed over ${handover}.` : `Handover ${handover}.`), ho && (ho.past ? `Handed over ${ho.year}.` : `Handover ${ho.year}.`)],
            [(p.beds || size) && `${p.beds ? `${p.beds}-bed ` : ''}${cat}${size ? ` from ${size} sq ft` : ''}.`, p.beds && `${p.beds}-bed ${cat}.`],
            p.soldOut
                ? ['Ask us about alternatives in the same area.', 'Ask about alternatives.']
                : ['Direct developer price, zero buyer commission.', 'Developer price, zero commission.', 'Zero commission.']
        ],
        ru: [
            [`${p.title}${dev ? ` от ${dev}` : ''}: ${place}.`, `${p.title}: ${place}.`],
            [p.soldOut ? 'Сейчас распродано.' : price && `Цена от ${price}.`],
            [plan && `${cap(plan)}.`],
            [handover && (ho.past ? `Сдан: ${handover}.` : `Сдача: ${handover}.`), ho && (ho.past ? `Сдан в ${ho.year}.` : `Сдача в ${ho.year}.`)],
            [(p.beds || size) && `${cap(cat)}${p.beds ? `, спален: ${p.beds}` : ''}${size ? `, от ${size} кв. футов` : ''}.`, p.beds && `${cap(cat)}, спален: ${p.beds}.`],
            p.soldOut
                ? ['Спросите нас об альтернативах в том же районе.', 'Спросите об альтернативах.']
                : ['Цена застройщика, без комиссии для покупателя.', 'Цена застройщика, без комиссии.', 'Без комиссии.']
        ],
        zh: [
            [`${p.title}${dev ? `（${dev}）` : ''}位于${place}。`, `${p.title}位于${place}。`],
            [p.soldOut ? '目前已售罄。' : price && `${price}起。`],
            [plan && `${plan}。`],
            [handover && (ho.past ? `已于${handover}交付。` : `${handover}交付。`), ho && `${ho.year}年交付。`],
            p.soldOut ? ['可咨询同区域替代项目。'] : ['开发商直售价，零佣金。', '零佣金。']
        ],
        ur: [
            [`${p.title}${dev ? ` (${dev})` : ''}، ${place}۔`, `${p.title}، ${place}۔`],
            [p.soldOut ? 'فی الحال فروخت ہو چکا۔' : price && `قیمت ${price} سے۔`],
            [plan && `${plan}۔`],
            [handover && (ho.past ? `حوالگی ہو چکی: ${handover}۔` : `حوالگی: ${handover}۔`), ho && `حوالگی ${ho.year}۔`],
            [(p.beds || size) && `${cat}${p.beds ? `، ${p.beds} بیڈروم` : ''}${size ? `، ${size} مربع فٹ سے` : ''}۔`, p.beds && `${cat}، ${p.beds} بیڈروم۔`],
            p.soldOut
                ? ['اسی علاقے میں متبادل کے بارے میں پوچھیں۔', 'متبادل کے بارے میں پوچھیں۔']
                : ['براہِ راست ڈیولپر قیمت، خریدار سے کوئی کمیشن نہیں۔', 'ڈیولپر قیمت، کوئی کمیشن نہیں۔', 'کوئی کمیشن نہیں۔']
        ]
    }[lang] || [];

    const max = lang === 'zh' ? 80 : 160;
    const min = lang === 'zh' ? 70 : 150;
    const sep = lang === 'zh' ? '' : ' ';
    let out = '';
    for (const options of slots) {
        for (const option of options.filter(Boolean)) {
            const next = out ? `${out}${sep}${option}` : option;
            if (next.length <= max) { out = next; break; }
        }
    }
    // Still short of the window: add true, general facts — longest that fits
    // first — rather than leave Google a snippet it will pad on its own.
    const lic = NAP.dldBrokerNumber;
    const fillers = {
        ar: ['استشارة مجانية.', `رخصة DLD ${lic}.`, 'وسيط مرخّص.', 'بدون عمولة.', `DLD ${lic}.`],
        en: ['Free consultation.', `DLD broker ${lic}.`, 'Licensed broker.', 'Zero commission.', `DLD ${lic}.`, 'No fees.'],
        ru: ['Бесплатная консультация.', 'Лицензированный брокер.', `Брокер DLD ${lic}.`, 'Без комиссии.', `DLD ${lic}.`],
        zh: ['免费咨询。', '持牌经纪。', '零佣金。', '土地局持牌。'],
        ur: ['مفت مشاورت۔', 'لائسنس یافتہ بروکر۔', `DLD بروکر ${lic}۔`, 'کوئی کمیشن نہیں۔', `DLD ${lic}۔`]
    }[lang] || [];
    for (const f of fillers) {
        if (out.length >= min) break;
        if (out.includes(f)) continue;
        const next = `${out}${sep}${f}`;
        if (next.length <= max) out = next;
    }
    return out || truncate(slots.flat().filter(Boolean).join(sep), max);
}

/**
 * The direct-answer paragraph, placed as the first prose on the page. Answer
 * engines lift the first self-contained paragraph that resolves the query;
 * this one names the project, developer, location, price, size and handover,
 * so a quote of it is a complete answer on its own.
 */
export function projectAnswer(project, lang = 'ar') {
    const p = normaliseProject(project);
    const price = fullPrice(p.priceMin, lang);
    const priceMax = p.priceMax > p.priceMin ? fullPrice(p.priceMax, lang) : null;
    const handover = handoverLabel(p.handover, lang);
    const brand = NAP.name[lang] || NAP.name.en;
    const place = placeLine(p, lang);
    const cat = categoryName(p.category, lang);
    const area = p.areaMin ? p.areaMin.toLocaleString('en-US') : '';
    const s = [];
    // The project's own payment plan — the sentence that used to claim a 1%
    // monthly plan for every project now states the real one.
    const planLine = p.soldOut ? null : planSentence(paymentPlan(project), lang);

    if (lang === 'ar') {
        s.push(`${p.title} مشروع سكني${p.handoverPast ? '' : ' على الخارطة'}${p.developer ? ` من تطوير ${p.developer}` : ''} في ${place}، ${UAE.ar}.`);
        if (p.category || p.beds || p.areaMin) {
            let u = `يضم ${cat}`;
            if (p.beds) u += ` بعدد غرف ${p.beds}`;
            if (area) u += ` تبدأ مساحاتها من ${area} قدم مربع`;
            s.push(u + '.');
        }
        if (price) s.push(`${p.soldOut ? 'كانت الأسعار تبدأ من' : 'تبدأ الأسعار من'} ${price}${priceMax ? ` وتصل إلى ${priceMax}` : ''}.`);
        if (handover) s.push(p.handoverPast ? `اكتمل تسليم المشروع في ${handover}.` : `التسليم المتوقّع ${handover}.`);
        if (planLine) s.push(planLine);
        s.push(p.soldOut
            ? `نفدت وحدات هذا المشروع حالياً، ويوفّر ${brand} مشاريع بديلة في المنطقة نفسها بسعر المطوّر.`
            : `يُباع عبر ${brand} بسعر المطوّر الرسمي دون عمولة على المشتري.`);
    } else if (lang === 'ru') {
        // Place names are not declined ("в Дубай" is wrong), so the location
        // goes after a colon instead of a preposition.
        s.push(`${p.title} — ${p.handoverPast ? 'сданный' : 'строящийся'} жилой проект${p.developer ? ` застройщика ${p.developer}` : ''}. Расположение: ${place}, ${UAE.ru}.`);
        if (p.category || p.beds || p.areaMin) {
            let u = `В проекте ${cat}`;
            if (p.beds) u += `, спален: ${p.beds}`;
            if (area) u += `, площадь от ${area} кв. футов`;
            s.push(u + '.');
        }
        if (price) s.push(`Цены ${p.soldOut ? 'начинались' : 'начинаются'} от ${price}${priceMax ? ` и доходят до ${priceMax}` : ''}.`);
        if (handover) s.push(p.handoverPast ? `Проект сдан в ${handover}.` : `Ожидаемая сдача — ${handover}.`);
        if (planLine) s.push(planLine);
        s.push(p.soldOut
            ? `Сейчас все юниты проданы; ${brand} подберёт альтернативы в том же районе по цене застройщика.`
            : `${brand} продаёт по официальной цене застройщика без комиссии для покупателя.`);
    } else if (lang === 'zh') {
        s.push(`${p.title}是位于${UAE.zh}${place}的${p.handoverPast ? '已交付' : '期房'}住宅项目${p.developer ? `，由${p.developer}开发` : ''}。`);
        if (p.category || p.beds || p.areaMin) {
            let u = `项目提供${cat}`;
            if (p.beds) u += `，卧室数${p.beds}`;
            if (area) u += `，面积${area}平方英尺起`;
            s.push(u + '。');
        }
        if (price) s.push(`价格${p.soldOut ? '曾' : ''}从${price}起${priceMax ? `，最高${priceMax}` : ''}。`);
        if (handover) s.push(p.handoverPast ? `项目已于${handover}交付。` : `预计${handover}交付。`);
        if (planLine) s.push(planLine);
        s.push(p.soldOut
            ? `该项目目前已售罄，${brand}可按开发商价格推荐同区域的替代项目。`
            : `${brand}按开发商官方价格销售，买家零佣金。`);
        return s.join('');
    } else if (lang === 'ur') {
        s.push(`${p.title} ${UAE.ur} کے ${place} میں${p.developer ? ` ${p.developer} کا` : ''} ${p.handoverPast ? 'مکمل' : 'آف پلان'} رہائشی پروجیکٹ ہے۔`);
        if (p.category || p.beds || p.areaMin) {
            let u = `اس میں ${cat}`;
            if (p.beds) u += `، بیڈرومز ${p.beds}`;
            if (area) u += `، رقبہ ${area} مربع فٹ سے`;
            s.push(u + ' دستیاب ہیں۔');
        }
        if (price) s.push(`قیمتیں ${price} سے ${p.soldOut ? 'شروع ہوتی تھیں' : 'شروع ہوتی ہیں'}${priceMax ? ` اور ${priceMax} تک جاتی ہیں` : ''}۔`);
        if (handover) s.push(p.handoverPast ? `پروجیکٹ کی حوالگی ${handover} میں ہو چکی۔` : `متوقع حوالگی ${handover}۔`);
        if (planLine) s.push(planLine);
        s.push(p.soldOut
            ? `اس پروجیکٹ کے یونٹس فروخت ہو چکے ہیں؛ ${brand} اسی علاقے میں ڈیولپر قیمت پر متبادل پیش کرتا ہے۔`
            : `${brand} اسے ڈیولپر کی سرکاری قیمت پر، خریدار سے کوئی کمیشن لیے بغیر فروخت کرتا ہے۔`);
    } else {
        s.push(`${p.title} is ${p.handoverPast ? 'a completed' : 'an off-plan'} residential development${p.developer ? ` by ${p.developer}` : ''} in ${place}, ${UAE.en}.`);
        if (p.category || p.beds || p.areaMin) {
            let u = `It offers ${cat}`;
            if (p.beds) u += ` with ${p.beds} bedroom${p.beds === '1' ? '' : 's'}`;
            if (area) u += ` from ${area} sq ft`;
            s.push(u + '.');
        }
        if (price) s.push(`Prices ${p.soldOut ? 'started' : 'start'} at ${price}${priceMax ? ` and reach ${priceMax}` : ''}.`);
        if (handover) s.push(p.handoverPast ? `The project was handed over in ${handover}.` : `Expected handover is ${handover}.`);
        if (planLine) s.push(planLine);
        s.push(p.soldOut
            ? `It is currently sold out; ${brand} offers alternatives in the same district at the developer price.`
            : `${brand} sells it at the official developer price with zero buyer commission.`);
    }
    return s.join(' ');
}

/** Headings and fact labels of a project page, shared by React and the prerenderer. */
const PROJECT_LABELS = {
    ar: {
        details: 'تفاصيل المشروع', developer: 'المطوّر', location: 'الموقع', priceFrom: 'السعر يبدأ من', sizeFrom: 'المساحة تبدأ من',
        bedrooms: 'غرف النوم', handover: 'التسليم المتوقّع', handedOver: 'سُلّم', unitType: 'نوع الوحدات', status: 'حالة المشروع',
        cta: 'اطلب عرض السعر من المطوّر مباشرة', faq: (t) => `أسئلة شائعة عن ${t}`, related: (place) => `مشاريع أخرى في ${place}`,
        allInArea: (name) => `كل مشاريع ${name} على الخارطة`, developerAll: (name) => `كل مشاريع ${name}`, similar: 'مشاريع مشابهة',
        browseAll: 'تصفّح كل مشاريع دبي وأبوظبي على الخارطة',
        byArea: 'المشاريع حسب المنطقة', faqLink: 'الأسئلة الشائعة عن الشراء على الخارطة', projects: 'المشاريع'
    },
    en: {
        details: 'Project details', developer: 'Developer', location: 'Location', priceFrom: 'Price from', sizeFrom: 'Size from',
        bedrooms: 'Bedrooms', handover: 'Expected handover', handedOver: 'Handed over', unitType: 'Unit type', status: 'Status',
        cta: 'Get the price list from the developer', faq: (t) => `${t} FAQ`, related: (place) => `Other projects in ${place}`,
        allInArea: (name) => `All off-plan projects in ${name}`, developerAll: (name) => `All ${name} projects`, similar: 'Similar projects', browseAll: 'Browse all Dubai & Abu Dhabi off-plan projects',
        byArea: 'Projects by area', faqLink: 'FAQ about buying off-plan', projects: 'Projects'
    },
    ru: {
        details: 'О проекте', developer: 'Застройщик', location: 'Расположение', priceFrom: 'Цена от', sizeFrom: 'Площадь от',
        bedrooms: 'Спальни', handover: 'Ожидаемая сдача', handedOver: 'Сдан', unitType: 'Тип юнитов', status: 'Статус',
        cta: 'Получить прайс застройщика', faq: (t) => `Вопросы о ${t}`, related: (place) => `Другие проекты: ${place}`,
        allInArea: (name) => `Все новостройки района ${name}`, developerAll: (name) => `Все проекты ${name}`, similar: 'Похожие проекты', browseAll: 'Все новостройки Дубая и Абу-Даби',
        byArea: 'Проекты по районам', faqLink: 'Вопросы о покупке на этапе строительства', projects: 'Проекты'
    },
    zh: {
        details: '项目详情', developer: '开发商', location: '位置', priceFrom: '起价', sizeFrom: '面积起',
        bedrooms: '卧室', handover: '预计交付', handedOver: '已交付', unitType: '户型', status: '状态',
        cta: '向开发商索取价目表', faq: (t) => `${t}常见问题`, related: (place) => `${place}的其他项目`,
        allInArea: (name) => `${name}全部期房项目`, developerAll: (name) => `${name}全部项目`, similar: '相似项目', browseAll: '浏览迪拜与阿布扎比全部期房项目',
        byArea: '按区域浏览项目', faqLink: '期房购买常见问题', projects: '项目'
    },
    ur: {
        details: 'پروجیکٹ کی تفصیلات', developer: 'ڈیولپر', location: 'مقام', priceFrom: 'قیمت شروع', sizeFrom: 'رقبہ شروع',
        bedrooms: 'بیڈرومز', handover: 'متوقع حوالگی', handedOver: 'حوالگی ہو چکی', unitType: 'یونٹ کی قسم', status: 'حیثیت',
        cta: 'ڈیولپر سے قیمتوں کی فہرست حاصل کریں', faq: (t) => `${t} — عمومی سوالات`, related: (place) => `${place} میں دیگر پروجیکٹس`,
        allInArea: (name) => `${name} کے تمام آف پلان پروجیکٹس`, developerAll: (name) => `${name} کے تمام پروجیکٹس`, similar: 'ملتے جلتے پروجیکٹس', browseAll: 'دبئی اور ابوظہبی کے تمام آف پلان پروجیکٹس',
        byArea: 'علاقے کے لحاظ سے پروجیکٹس', faqLink: 'آف پلان خریداری کے عمومی سوالات', projects: 'پروجیکٹس'
    }
};
export const projectLabels = (lang) => PROJECT_LABELS[lang] || PROJECT_LABELS.en;

/** "JVC, Dubai" in the page language — exported for the page components. */
export const projectPlace = (project, lang) => placeLine(normaliseProject(project), lang);

/** "1,2,3" -> "1-3"; "2" -> "2"; null -> "". */
function bedsRange(raw) {
    const nums = String(raw ?? '')
        .split(/[,\s]+/)
        .map(Number)
        .filter((n) => isFinite(n) && n > 0);
    if (nums.length === 0) return '';
    const lo = Math.min(...nums), hi = Math.max(...nums);
    return lo === hi ? String(lo) : `${lo}-${hi}`;
}
